import { useState, useEffect } from "react";
import {
  Globe,
  Copy,
  CheckCircle,
  Loader2,
  AlertCircle,
  ExternalLink,
  Star,
  Trash2,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

import { customDomainsApi, CustomDomain } from "@/lib/api/custom-domains";

interface CustomDomainModalProps {
  isOpen: boolean;
  projectId: string;
  onClose: () => void;
  onDomainsChange?: () => void;
}

export function CustomDomainModal({
  isOpen,
  projectId,
  onClose,
  onDomainsChange,
}: CustomDomainModalProps) {
  const [domains, setDomains] = useState<CustomDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [newDomain, setNewDomain] = useState("");
  const [adding, setAdding] = useState(false);
  const [autoCheckingDomains, setAutoCheckingDomains] = useState<Set<string>>(
    new Set()
  );
  const [verifyingDomains, setVerifyingDomains] = useState<Set<string>>(
    new Set()
  );
  const [deletingDomains, setDeletingDomains] = useState<Set<string>>(
    new Set()
  );
  const [settingPrimary, setSettingPrimary] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadDomains();
    }
  }, [projectId, isOpen]);

  // Auto-polling for domains in verifying/pending status
  useEffect(() => {
    if (autoCheckingDomains.size === 0) return;

    const interval = setInterval(async () => {
      for (const domainId of autoCheckingDomains) {
        try {
          const updated = await customDomainsApi.verify(projectId, domainId);
          setDomains((prev) =>
            prev.map((d) => (d.id === domainId ? updated : d))
          );

          if (updated.status === "active") {
            setAutoCheckingDomains((prev) => {
              const next = new Set(prev);
              next.delete(domainId);
              return next;
            });
            toast.success(`${updated.domain} verified! 🎉`);
          }
        } catch (error) {
          // Silently fail, user can manually verify
        }
      }
    }, 10000); // Check every 10 seconds

    return () => clearInterval(interval);
  }, [autoCheckingDomains, projectId]);

  const loadDomains = async () => {
    try {
      setLoading(true);
      const data = await customDomainsApi.list(projectId);
      setDomains(data);
    } catch (error: any) {
      toast.error("Failed to load custom domains");
    } finally {
      setLoading(false);
    }
  };

  const handleAddDomain = async () => {
    if (!newDomain.trim()) {
      toast.error("Please enter a domain");
      return;
    }

    setAdding(true);
    try {
      const domain = await customDomainsApi.create(projectId, newDomain.trim());
      setDomains([domain, ...domains]);
      setNewDomain("");
      toast.success("Domain added! Add the DNS records below.");
      // Notify parent component to refresh
      onDomainsChange?.();
    } catch (error: any) {
      toast.error(error.message || "Failed to add domain");
    } finally {
      setAdding(false);
    }
  };

  const handleVerify = async (domainId: string) => {
    setVerifyingDomains((prev) => new Set(prev).add(domainId));
    try {
      const updated = await customDomainsApi.verify(projectId, domainId);
      setDomains(domains.map((d) => (d.id === domainId ? updated : d)));

      if (updated.status === "active") {
        toast.success("Domain verified successfully!");
        setAutoCheckingDomains((prev) => {
          const next = new Set(prev);
          next.delete(domainId);
          return next;
        });
      } else if (updated.status === "failed") {
        toast.error("Verification failed. Check DNS records.");
        setAutoCheckingDomains((prev) => {
          const next = new Set(prev);
          next.delete(domainId);
          return next;
        });
      } else {
        // Start auto-checking
        setAutoCheckingDomains((prev) => new Set(prev).add(domainId));
        toast("Checking DNS records... We'll notify you when ready.");
      }
    } catch (error: any) {
      toast.error("Failed to verify domain");
    } finally {
      setVerifyingDomains((prev) => {
        const next = new Set(prev);
        next.delete(domainId);
        return next;
      });
    }
  };

  const stopAutoCheck = (domainId: string) => {
    setAutoCheckingDomains((prev) => {
      const next = new Set(prev);
      next.delete(domainId);
      return next;
    });
    toast("Auto-check stopped. You can verify manually anytime.");
  };

  const handleSetPrimary = async (domainId: string) => {
    setSettingPrimary(domainId);
    try {
      await customDomainsApi.setPrimary(projectId, domainId);
      setDomains(domains.map((d) => ({ ...d, isPrimary: d.id === domainId })));
      toast.success(
        "Primary domain set! Your project is now live on this domain."
      );
      // Notify parent component to refresh
      onDomainsChange?.();
    } catch (error: any) {
      toast.error(error.message || "Failed to set primary domain");
    } finally {
      setSettingPrimary(null);
    }
  };

  const handleDelete = async (domainId: string) => {
    setDeletingDomains((prev) => new Set(prev).add(domainId));
    try {
      await customDomainsApi.delete(projectId, domainId);
      setDomains(domains.filter((d) => d.id !== domainId));
      setAutoCheckingDomains((prev) => {
        const next = new Set(prev);
        next.delete(domainId);
        return next;
      });
      toast.success("Domain removed");
      // Notify parent component to refresh
      onDomainsChange?.();
    } catch (error: any) {
      toast.error("Failed to remove domain");
    } finally {
      setDeletingDomains((prev) => {
        const next = new Set(prev);
        next.delete(domainId);
        return next;
      });
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  const copyAllDNS = (records: any[]) => {
    const text = records
      .map(
        (r) =>
          `Type: ${r.type}\nName: ${r.name}\nValue: ${r.value}\nTTL: ${r.ttl}\n`
      )
      .join("\n");
    navigator.clipboard.writeText(text);
    toast.success("All DNS records copied!");
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[520px] w-full max-w-[90vw] rounded-lg p-0 gap-0">
        {/* Header */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/10">
              <Globe className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <DialogTitle className="text-lg font-semibold">
                Custom Domains
              </DialogTitle>
              <DialogDescription className="text-sm mt-0.5">
                Connect your own domain to this project
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto max-h-[60vh] px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {/* Empty State */}
              {domains.length === 0 && (
                <div className="text-center py-8 px-4">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted/50 mx-auto mb-4">
                    <Globe className="w-8 h-8 text-muted-foreground/50" />
                  </div>
                  <h3 className="text-sm font-medium mb-1">
                    No custom domains yet
                  </h3>
                  <p className="text-xs text-muted-foreground mb-6">
                    Add your first domain to make your project accessible via
                    your own URL
                  </p>
                </div>
              )}

              {/* Existing Domains */}
              {domains.length > 0 && (
                <div className="space-y-3">
                  {domains.map((domain) => (
                    <DomainCard
                      key={domain.id}
                      domain={domain}
                      isAutoChecking={autoCheckingDomains.has(domain.id)}
                      isVerifying={verifyingDomains.has(domain.id)}
                      isDeleting={deletingDomains.has(domain.id)}
                      isSettingPrimary={settingPrimary === domain.id}
                      onVerify={() => handleVerify(domain.id)}
                      onStopAutoCheck={() => stopAutoCheck(domain.id)}
                      onSetPrimary={() => handleSetPrimary(domain.id)}
                      onDelete={() => handleDelete(domain.id)}
                      onCopy={copyToClipboard}
                      onCopyAll={copyAllDNS}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Add New Domain Form - Fixed at bottom */}
        <div className="px-6 py-4 border-t border-border bg-muted/20">
          <div className="flex items-center gap-2 mb-3">
            <Globe className="w-4 h-4 text-muted-foreground" />
            <h3 className="text-sm font-medium">Add New Domain</h3>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              placeholder="dashboard.mysite.com"
              className="flex-1 h-10 px-3 bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent text-sm transition-all"
              onKeyDown={(e) => e.key === "Enter" && handleAddDomain()}
            />
            <Button
              onClick={handleAddDomain}
              disabled={adding || !newDomain.trim()}
              size="sm"
              className="h-10 px-4 bg-[#625bff] hover:bg-[#564eff]"
            >
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add"}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Enter a subdomain you own (e.g., app.example.com)
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Separate domain card component for cleaner code
interface DomainCardProps {
  domain: CustomDomain;
  isAutoChecking: boolean;
  isVerifying: boolean;
  isDeleting: boolean;
  isSettingPrimary: boolean;
  onVerify: () => void;
  onStopAutoCheck: () => void;
  onSetPrimary: () => void;
  onDelete: () => void;
  onCopy: (text: string, label: string) => void;
  onCopyAll: (records: any[]) => void;
}

function DomainCard({
  domain,
  isAutoChecking,
  isVerifying,
  isDeleting,
  isSettingPrimary,
  onVerify,
  onStopAutoCheck,
  onSetPrimary,
  onDelete,
  onCopy,
}: DomainCardProps) {
  const [showDNS, setShowDNS] = useState(
    domain.status === "pending" || domain.status === "verifying"
  );
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const getStatusDisplay = () => {
    if (domain.isPrimary && domain.status === "active") {
      return (
        <div className="flex items-center gap-1.5 text-xs px-2 py-1 rounded bg-purple-500/20 text-purple-300 border border-purple-500/30 font-medium">
          <Star className="w-3 h-3 fill-purple-300" />
          Primary
        </div>
      );
    }

    const statusMap = {
      pending: {
        icon: AlertCircle,
        label: "Waiting for DNS",
        color:
          "text-yellow-700 bg-yellow-100 border-yellow-200 dark:text-yellow-400 dark:bg-yellow-500/10 dark:border-yellow-500/20",
        spin: false,
      },
      verifying: {
        icon: Loader2,
        label: "Verifying...",
        color:
          "text-blue-700 bg-blue-100 border-blue-200 dark:text-blue-400 dark:bg-blue-500/10 dark:border-blue-500/20",
        spin: true,
      },
      active: {
        icon: CheckCircle,
        label: "Active",
        color:
          "text-green-700 bg-green-100 border-green-200 dark:text-green-400 dark:bg-green-500/10 dark:border-green-500/20",
        spin: false,
      },
      failed: {
        icon: AlertCircle,
        label: "Failed",
        color:
          "text-red-700 bg-red-100 border-red-200 dark:text-red-400 dark:bg-red-500/10 dark:border-red-500/20",
        spin: false,
      },
    };

    const status =
      statusMap[domain.status as keyof typeof statusMap] || statusMap.pending;
    const Icon = status.icon;

    return (
      <div
        className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded border font-medium ${status.color}`}
      >
        <Icon className={`w-3 h-3 ${status.spin ? "animate-spin" : ""}`} />
        {status.label}
      </div>
    );
  };

  return (
    <div className="border border-border rounded-lg p-4 bg-card/30 hover:bg-card/50 transition-colors">
      {/* Domain Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            {domain.status === "active" ? (
              <a
                href={`https://${domain.domain}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-base hover:text-blue-500 dark:hover:text-blue-400 transition-colors flex items-center gap-1.5 group"
              >
                {domain.domain}
                <ExternalLink className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
            ) : (
              <span className="font-medium text-base">{domain.domain}</span>
            )}
            {getStatusDisplay()}
          </div>
        </div>
        {!showDeleteConfirm ? (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            disabled={isDeleting}
            className="text-xs text-muted-foreground hover:text-red-400 transition-colors ml-2 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Delete domain"
          >
            {isDeleting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
          </button>
        ) : (
          <div className="flex items-center gap-1 ml-2">
            <button
              onClick={() => {
                onDelete();
                setShowDeleteConfirm(false);
              }}
              disabled={isDeleting}
              className="text-xs px-2 py-1 rounded bg-red-100 hover:bg-red-200 text-red-700 dark:bg-red-500/20 dark:hover:bg-red-500/30 dark:text-red-400 border border-red-300 dark:border-red-500/30 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              {isDeleting && <Loader2 className="w-3 h-3 animate-spin" />}
              Delete
            </button>
            <button
              onClick={() => setShowDeleteConfirm(false)}
              disabled={isDeleting}
              className="text-xs px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 dark:bg-muted dark:hover:bg-muted/80 text-gray-700 dark:text-foreground transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Pending: Show DNS + Check Button */}
      {domain.status === "pending" && (
        <div className="space-y-3">
          <button
            onClick={() => setShowDNS(!showDNS)}
            className="w-full flex items-center justify-between text-sm text-muted-foreground hover:text-foreground transition-colors group"
          >
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
              <span>Add these 2 DNS records to your domain provider</span>
            </div>
            {showDNS ? (
              <ChevronUp className="w-4 h-4 group-hover:text-foreground" />
            ) : (
              <ChevronDown className="w-4 h-4 group-hover:text-foreground" />
            )}
          </button>

          {showDNS && (
            <div className="space-y-2.5">
              {domain.dnsRecords.map((record, index) => (
                <div
                  key={index}
                  className="bg-background border border-border rounded-lg p-3"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold px-2 py-0.5 rounded bg-blue-100 text-blue-700 border border-blue-200 dark:bg-blue-500/20 dark:text-blue-300 dark:border-blue-500/30">
                        {record.type}
                      </span>
                      {record.purpose && (
                        <span className="text-xs text-muted-foreground">
                          {record.purpose}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-muted-foreground font-medium">
                          Name
                        </span>
                        <button
                          onClick={() => onCopy(record.name, "Name")}
                          className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-1 transition-colors"
                        >
                          <Copy className="w-3 h-3" />
                          Copy
                        </button>
                      </div>
                      <code className="block text-xs bg-muted px-2 py-1.5 rounded break-all font-mono">
                        {record.name}
                      </code>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-muted-foreground font-medium">
                          Value
                        </span>
                        <button
                          onClick={() => onCopy(record.value, "Value")}
                          className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-1 transition-colors"
                        >
                          <Copy className="w-3 h-3" />
                          Copy
                        </button>
                      </div>
                      <code className="block text-xs bg-muted px-2 py-1.5 rounded break-all font-mono">
                        {record.value}
                      </code>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-end pt-2">
            <button
              onClick={onVerify}
              disabled={isVerifying}
              className="text-xs px-3 py-1.5 rounded bg-[#625bff] hover:bg-[#564eff] transition-colors font-medium text-white flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isVerifying ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Checking...
                </>
              ) : (
                <>
                  <CheckCircle className="w-3.5 h-3.5" />
                  Check DNS
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Verifying: Auto-checking status */}
      {domain.status === "verifying" && (
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
          <div className="flex items-start gap-2 text-sm">
            <Loader2 className="w-4 h-4 text-blue-400 animate-spin flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-blue-400 font-medium mb-1">
                Checking DNS records...
              </p>
              {isAutoChecking && (
                <p className="text-xs text-blue-300/70">
                  We'll notify you when it's ready (checking every 10s)
                </p>
              )}
            </div>
          </div>
          {isAutoChecking && (
            <button
              onClick={onStopAutoCheck}
              className="text-xs text-blue-400 hover:text-blue-300 mt-2 font-medium"
            >
              Stop checking
            </button>
          )}
        </div>
      )}

      {/* Active: Show primary option or visit link */}
      {domain.status === "active" && domain.sslStatus === "active" && (
        <div className="bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/20 rounded-lg p-2.5">
          {!domain.isPrimary ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                <span className="text-sm text-green-700 dark:text-green-400 font-medium">
                  Domain ready with SSL
                </span>
              </div>
              <button
                onClick={onSetPrimary}
                disabled={isSettingPrimary}
                className="text-xs px-3 py-1.5 rounded bg-[#625bff] hover:bg-[#564eff] text-white transition-colors font-medium flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSettingPrimary ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Setting...
                  </>
                ) : (
                  "Use this domain"
                )}
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
              <span className="text-sm text-green-700 dark:text-green-400 font-medium">
                Your project is live on this domain
              </span>
            </div>
          )}
        </div>
      )}

      {/* Active but no SSL yet */}
      {domain.status === "active" && domain.sslStatus !== "active" && (
        <div className="bg-orange-50 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/20 rounded-lg p-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-start gap-2 flex-1">
              <AlertCircle className="w-4 h-4 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-orange-700 dark:text-orange-400 font-medium mb-0.5">
                  SSL certificate is being provisioned
                </p>
                <p className="text-xs text-orange-600/80 dark:text-orange-300/70">
                  Please check back in 5-10 minutes
                </p>
              </div>
            </div>
            <button
              onClick={onVerify}
              disabled={isVerifying}
              className="text-xs px-3 py-1.5 rounded bg-gray-900 hover:bg-gray-800 text-white transition-colors font-medium flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
            >
              {isVerifying ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Checking...
                </>
              ) : (
                <>
                  <CheckCircle className="w-3.5 h-3.5" />
                  Refresh Status
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Failed: Show error */}
      {domain.status === "failed" && domain.verificationErrors && (
        <div className="flex items-start gap-2 text-sm text-red-400 bg-red-500/10 p-3 rounded border border-red-500/20">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="mb-1">Verification failed</p>
            <p className="text-xs">{domain.verificationErrors.join(", ")}</p>
          </div>
        </div>
      )}
    </div>
  );
}
